import createUniveralCookie from './create-universal-cookie';
import createAuth0Client from "@auth0/auth0-spa-js";

let errorHandlerFunc;
let instanceResolve;
let instance = new Promise(resolve => {
    instanceResolve = resolve;
});

const REFRESH_INTERVAL = 5 * 60 * 1000;

export const AuthenticationErrorCodes = {
    MISSING_OR_MALFORMED_COOKIE: 'missingOrMalformedCookie',
    NOT_SIGNED_IN: 'notSignedIn',
    REFRESH_TOKEN_FAILED: 'refreshTokenFailed',
    AUTH0_REFRESH_FAILED: 'auth0RefreshFailed',
};

const gydaCookie = {
  token: "gydaToken",
  remember: "gydaRemember",
};

export class AuthenticationClient {
    constructor({ baseUri, identityUri, cookieDomain, auth0_domain, auth0_audience, auth0_client_id }) {
        this.baseUri = baseUri;
        this.identityUri = identityUri;
        this.cookieDomain = cookieDomain;
        this.auth0_domain = auth0_domain;
        this.auth0_audience = auth0_audience;
        this.auth0_client_id = auth0_client_id;
        this.token = null;
        this.userCompanyPromise = null;
        this.initialized = false;

        this._cookie = new createUniveralCookie();

        this.init();
        instanceResolve(this);
    }

    init() {
        if(this.initialized) {
            return;
        }
        this.initialized = true;

        this.token = this._getInitialToken();
        this.auth0Client = this._getAuth0Client();
        this.refreshToken();
        installRefreshInterval(this.refreshToken.bind(this));
    }

    async getCurrentUserId() {
        if(!this.userCompanyPromise) {
            this.userCompanyPromise = fetch(`${this.identityUri}/`, {
                headers: {
                    Authorization: this.token
                }
            })
                .then(response => {
                    if(!response.ok) {
                        throw new Error('AuthenticationClient failed to get user information.');
                    }
                    return response.json();
                })
                .catch(() => {
                    this.userCompanyPromise = null;
                });
        }
        return this.userCompanyPromise
            .then(payload => {
                return payload.user._id;
            });
    }

    async getCurrentCompanyId() {
        if(!this.userCompanyPromise) {
            this.userCompanyPromise = fetch(`${this.identityUri}/`, {
                headers: {
                    Authorization: this.token
                }
            })
                .then(response => {
                    if(!response.ok) {
                        throw new Error('AuthenticationClient failed to get company information.');
                    }
                    return response.json();
                })
                .catch(() => {
                    this.userCompanyPromise = null;
                });
        }
        return this.userCompanyPromise
            .then(payload => {
                return payload.company._id;
            });
    }

    async getToken() {
        if(!this.token) {
            this.throwError(AuthenticationErrorCodes.NOT_SIGNED_IN);
        }

        return this.token;
    }

    async refreshToken() {
        if(!this.refreshPromise) {
            const gydaRemember = this._cookie.get('gydaRemember', {domain: this.cookieDomain});
            this.refreshPromise = fetch(`${this.baseUri}/auth/resetToken?rememberMe=${gydaRemember === "1"}`, {
                headers: {
                    Authorization: this.token
                }
            })
                .then(response => {
                    if (response.ok) {
                        // If the response is in the range of 200-299 consider is successful and return the response.
                        return response.json();
                    } else if (response.status === 401) {
                        // If the reponse returns a 401 we can assume our request lacks valid authentication credentials based off
                        // the current gydaToken.
                        throw AuthenticationErrorCodes.MISSING_OR_MALFORMED_COOKIE;
                    } else {
                        // If the response is not ok throw an error to let the client know.
                        // This check may be reduntant due to the .catch
                        throw AuthenticationErrorCodes.REFRESH_TOKEN_FAILED;
                    }
                })
                .then((response) => {
                    this.token = response.token;

                    let expireDays = 1;
                    if(gydaRemember === "1"){
                        expireDays = 7;
                    }
                    var exDate = new Date();
                    exDate.setDate(exDate.getDate() + expireDays);

                    // clear cookie with default path/domain if still exists
                    this._cookie.remove(gydaCookie.token, { domain: this.cookieDomain });
                    this._cookie.remove(gydaCookie.remember, { domain: this.cookieDomain });

                    const cookieSettings = {
                        expires: exDate,
                        path: '/',
                        domain: this.cookieDomain,
                        secure: true
                    }

                    this._cookie.set(gydaCookie.token, JSON.stringify(response), cookieSettings);
                    this._cookie.set(gydaCookie.remember, gydaRemember, cookieSettings);
                    this.refreshPromise = null;
                }).then(() => {
                    return this.refreshAuth0Token();
                })
                .catch((e) => {
                    let error;
                    if(e.message && e.message.indexOf(AuthenticationErrorCodes.AUTH0_REFRESH_FAILED) > -1) {
                        console.warn('Auth0 refresh failed fail silently');
                        return;
                    }
                    if (Object.values(AuthenticationErrorCodes).indexOf(e) > -1) {
                        error = e;
                    } else {
                        error = AuthenticationErrorCodes.REFRESH_TOKEN_FAILED;
                    }

                    this.throwError(error);
                });
        }
        return this.refreshPromise;
    }

    async _getAuth0Client() {
        if(!this.auth0Client) {
            this.auth0Client = await createAuth0Client({
                domain: this.auth0_domain,
                audience: this.auth0_audience,
                client_id: this.auth0_client_id,
                useRefreshTokens: true
            });
        }

        return this.auth0Client;
    }

    async refreshAuth0Token() {
        try {
            const client = await this._getAuth0Client();
            return await client.getTokenSilently();
        } catch (e) {
            this.throwError(AuthenticationErrorCodes.AUTH0_REFRESH_FAILED);
        }

    }

    _getInitialToken() {
        try {
            const cookieValue = this._cookie.get('gydaToken', {domain: this.cookieDomain});
            return cookieValue.token;
        } catch(error) {
            this.throwError(AuthenticationErrorCodes.MISSING_OR_MALFORMED_COOKIE);
        }
    }

    throwError(reason) {
        if(errorHandlerFunc) {
            errorHandlerFunc(reason);
        } else {
            throw new Error(`AuthenticationClient - No error handler function registered. ErrorCode: ${reason}`);
        }
    }

    static onError(passedErrorHandlerFunc) {
        if(errorHandlerFunc) {
            throw new Error('AuthenticationClient.onError can only be invoked once.');
        } else {
            errorHandlerFunc = passedErrorHandlerFunc;
        }
    }

    static async getInstance() {
        return instance;
    }
}

function installRefreshInterval(refreshToken) {
    setInterval(refreshToken, REFRESH_INTERVAL);
}
